import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { translate } from 'i18n/jhipster-language';
import { TabView, TabPanel } from 'primereact/tabview';
import { Skeleton } from 'primereact/skeleton';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import Timeline from './../../shared/ui/timeline';
import CardStatementTab from './card-statement';
import PRow from 'shared/ui/prow';
import PCol from 'shared/ui/pcol';
import OutPutPair from 'shared/ui/output-pair';
import { FaPlusCircle, FaCheckCircle, FaLock, FaLockOpen, FaTimesCircle, FaCircle, FaExclamationTriangle } from 'react-icons/fa';
import moment from 'moment';
import { APP_LOCAL_DATETIME_FORMAT } from 'app/config/constants';
import { validateRequiredDescription, validateOptionalDescription } from 'shared/lib/validation/search-filters';
import { FIELD_CONSTRAINTS } from 'shared/lib/validation/field-constraints';
import { CardFreezeRequestTab as CardFreezeRequest, CardUnfreezeRequestTab as CardUnfreezeRequest, } from './card-status-requests-tab';
import { CardReplacementRequestTab } from './card-replacement-requests-tab';
import { cardDefaultValue } from '@entities/card/model';
import { Button } from 'primereact/button';
import { cardQueryKeys, fetchCard, fetchCardLifecycleEvents, suspendCardRequest, unsuspendCardRequest, hotlistCardRequest, } from './card-query';
const yn = (v) => v == null ? '' : v ? translate('form.yes') : translate('form.no');
const formatDt = (v) => v ? moment(v).format(APP_LOCAL_DATETIME_FORMAT) : '';
function mapLifecycleToTimeline(raw) {
    const getEventTitle = (type) => {
        switch (type) {
            case 'CREATED':
                return translate('card.event.created');
            case 'ACTIVATED':
                return translate('card.event.activated');
            case 'FROZEN':
                return translate('card.event.frozen');
            case 'UNFROZEN':
                return translate('card.event.unfrozen');
            case 'CLOSED':
                return translate('card.event.closed');
            case 'HOTLISTED':
                return translate('card.event.hotlisted');
            default:
                return type;
        }
    };
    const getIcon = (type) => {
        switch (type) {
            case 'CREATED':
                return _jsx(FaPlusCircle, {});
            case 'ACTIVATED':
                return _jsx(FaCheckCircle, {});
            case 'FROZEN':
                return _jsx(FaLock, {});
            case 'UNFROZEN':
                return _jsx(FaLockOpen, {});
            case 'CLOSED':
                return _jsx(FaTimesCircle, {});
            case 'HOTLISTED':
                return _jsx(FaExclamationTriangle, {});
            default:
                return _jsx(FaCircle, {});
        }
    };
    const getColor = (type) => {
        switch (type) {
            case 'CREATED':
                return '#2196F3';
            case 'ACTIVATED':
                return '#4CAF50';
            case 'FROZEN':
                return '#FF9800';
            case 'UNFROZEN':
                return '#009688';
            case 'CLOSED':
                return '#F44336';
            case 'HOTLISTED':
                return '#E91E63';
            default:
                return '#607D8B';
        }
    };
    return [...raw]
        .sort((a, b) => new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime())
        .map(e => ({
        title: getEventTitle(e.eventType),
        description: e.reasonCode
            ? `Reason: ${e.reasonCode}${e.sourceChannel ? ` • Channel: ${e.sourceChannel}` : ''}`
            : e.sourceChannel
                ? `Channel: ${e.sourceChannel}`
                : '',
        date: formatDt(e.eventAt),
        icon: getIcon(e.eventType),
        statusColor: getColor(e.eventType),
    }));
}
const CardDetails = ({ cardId, onClose, isPageView }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [suspendDialogVisible, setSuspendDialogVisible] = useState(false);
    const [suspendDialogMode, setSuspendDialogMode] = useState('suspend');
    const [suspendReason, setSuspendReason] = useState('');
    const [suspendReasonTouched, setSuspendReasonTouched] = useState(false);
    const [hotlistDialogVisible, setHotlistDialogVisible] = useState(false);
    const [hotlistReason, setHotlistReason] = useState('');
    const [hotlistReasonTouched, setHotlistReasonTouched] = useState(false);
    const { data: cardEntityData, isPending: loadingEntity, isError: cardLoadError, refetch: refetchCard, } = useQuery({
        queryKey: cardQueryKeys.detail(cardId),
        queryFn: () => fetchCard(cardId),
        enabled: cardId > 0,
    });
    const { data: lifecycleRaw = [], isPending: loadingLifecycle } = useQuery({
        queryKey: cardQueryKeys.lifecycle(cardId),
        queryFn: () => fetchCardLifecycleEvents(cardId),
        enabled: cardId > 0,
    });
    const lifecycleEvents = useMemo(() => mapLifecycleToTimeline(lifecycleRaw), [lifecycleRaw]);
    const suspendMutation = useMutation({
        mutationFn: ({ id, reason }) => suspendCardRequest(id, reason),
        onMutate: async ({ id }) => {
            await queryClient.cancelQueries({ queryKey: cardQueryKeys.detail(id) });
            const previous = queryClient.getQueryData(cardQueryKeys.detail(id));
            if (previous?.id) {
                queryClient.setQueryData(cardQueryKeys.detail(id), {
                    ...previous,
                    status: 'SUSPENDED',
                });
            }
            return { previous };
        },
        onError: (_err, { id }, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData(cardQueryKeys.detail(id), ctx.previous);
            }
        },
        onSuccess: () => {
            setSuspendDialogVisible(false);
            setSuspendDialogMode('suspend');
            setSuspendReason('');
            setSuspendReasonTouched(false);
        },
        onSettled: (_d, _e, { id }) => {
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lifecycle(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lists() });
        },
    });
    const unsuspendMutation = useMutation({
        mutationFn: ({ id, reason }) => unsuspendCardRequest(id, reason),
        onMutate: async ({ id }) => {
            await queryClient.cancelQueries({ queryKey: cardQueryKeys.detail(id) });
            const previous = queryClient.getQueryData(cardQueryKeys.detail(id));
            if (previous?.id) {
                queryClient.setQueryData(cardQueryKeys.detail(id), {
                    ...previous,
                    status: 'ACTIVE',
                });
            }
            return { previous };
        },
        onError: (_err, { id }, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData(cardQueryKeys.detail(id), ctx.previous);
            }
        },
        onSuccess: () => {
            setSuspendDialogVisible(false);
            setSuspendDialogMode('suspend');
            setSuspendReason('');
            setSuspendReasonTouched(false);
        },
        onSettled: (_d, _e, { id }) => {
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lifecycle(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lists() });
        },
    });
    const hotlistMutation = useMutation({
        mutationFn: ({ id, reason }) => hotlistCardRequest(id, reason),
        onMutate: async ({ id }) => {
            await queryClient.cancelQueries({ queryKey: cardQueryKeys.detail(id) });
            const previous = queryClient.getQueryData(cardQueryKeys.detail(id));
            if (previous?.id) {
                queryClient.setQueryData(cardQueryKeys.detail(id), {
                    ...previous,
                    status: 'HOTLISTED',
                });
            }
            return { previous };
        },
        onError: (_err, { id }, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData(cardQueryKeys.detail(id), ctx.previous);
            }
        },
        onSuccess: () => {
            setHotlistDialogVisible(false);
            setHotlistReason('');
            setHotlistReasonTouched(false);
        },
        onSettled: (_d, _e, { id }) => {
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.detail(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lifecycle(id) });
            void queryClient.invalidateQueries({ queryKey: cardQueryKeys.lists() });
        },
    });
    const lifecycleBusy = suspendMutation.isPending || unsuspendMutation.isPending || hotlistMutation.isPending;
    const c = (cardEntityData ?? cardDefaultValue);
    const hasCard = !loadingEntity && c?.id != null;
    const isHotlisted = c.status === 'HOTLISTED';
    const suspendReasonRequired = suspendDialogMode === 'suspend';
    const suspendReasonError = suspendDialogMode === 'suspend'
        ? suspendReasonTouched
            ? validateRequiredDescription(suspendReason)
            : null
        : suspendReasonTouched
            ? validateOptionalDescription(suspendReason)
            : null;
    const suspendReasonInvalid = !!suspendReasonError;
    const openSuspendDialog = () => {
        setSuspendDialogMode('suspend');
        setSuspendReason('');
        setSuspendReasonTouched(false);
        setSuspendDialogVisible(true);
    };
    const openUnsuspendDialog = () => {
        setSuspendDialogMode('unsuspend');
        setSuspendReason('');
        setSuspendReasonTouched(false);
        setSuspendDialogVisible(true);
    };
    const openHotlistDialog = () => {
        setHotlistReason('');
        setHotlistReasonTouched(false);
        setHotlistDialogVisible(true);
    };
    const hotlistReasonError = hotlistReasonTouched ? validateRequiredDescription(hotlistReason) : null;
    const hotlistReasonInvalid = !!hotlistReasonError;
    const confirmHotlistDialog = () => {
        setHotlistReasonTouched(true);
        if (!c.id) {
            return;
        }
        const err = validateRequiredDescription(hotlistReason);
        if (err)
            return;
        hotlistMutation.mutate({ id: c.id, reason: hotlistReason.trim() });
    };
    const confirmLifecycleDialog = () => {
        setSuspendReasonTouched(true);
        if (!c.id) {
            return;
        }
        if (suspendDialogMode === 'suspend') {
            const err = validateRequiredDescription(suspendReason);
            if (err)
                return;
            suspendMutation.mutate({ id: c.id, reason: suspendReason.trim() });
            return;
        }
        const optionalErr = validateOptionalDescription(suspendReason);
        if (optionalErr)
            return;
        const r = suspendReason.trim();
        unsuspendMutation.mutate({ id: c.id, reason: r || undefined });
    };
    const p = c?.product;
    const s = p?.schema;
    const currency = c?.currency;
    if (cardId > 0 && cardLoadError && !cardEntityData) {
        return (_jsx(PRow, { style: { width: '100%' }, className: "p-mt-2 p-px-2", children: _jsxs(PCol, { col: "12", children: [_jsx(Message, { severity: "error", className: "mb-3", text: translate('error.internal_error') }), _jsx(Button, { type: "button", icon: "pi pi-refresh", className: "p-button-outlined", onClick: () => void refetchCard() })] }) }));
    }
    const summaryFields = !hasCard ? (_jsx(PCol, { col: "12", children: _jsx(Skeleton, { height: "2rem" }) })) : (_jsx(PCol, { col: "12", className: "mt-4 pt-4", children: _jsxs(PRow, { children: [_jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardReference", value: c.cardReference ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.maskedPan", value: c.maskedPan ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.panLast4", value: c.panLast4 ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardHolderName", value: c.cardHolderName ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.customerReference", value: c.customerReference ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.fundingReference", value: c.fundingReference ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.productCode", value: p?.productCode ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.productName", value: p?.productName ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.currency", value: currency?.code ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.currencyName", value: currency?.name ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardType", value: c.cardType ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardForm", value: c.cardForm ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.status", value: c.status ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.issueDate", value: c.issueDate ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.activationDate", value: c.activationDate ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.expiryDate", value: c.expiryDate ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.createdAt", value: formatDt(c.createdAt) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.updatedAt", value: formatDt(c.updatedDate) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.atmAllowed", value: yn(c.atmAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.posAllowed", value: yn(c.posAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.ecommerceAllowed", value: yn(c.ecommerceAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.contactlessAllowed", value: yn(c.contactlessAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.internationalAllowed", value: yn(c.internationalAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cashWithdrawalAllowed", value: yn(c.cashWithdrawalAllowed) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.interchangeScope", value: c.interchangeScope ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.authorizationRequirement", value: c.authorizationRequirement ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.usageRestriction", value: c.usageRestriction ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.commissionGroupCode", value: c.commissionGroupCode ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.rewardGroupCode", value: c.rewardGroupCode ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.pricingGroupCode", value: c.pricingGroupCode ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.schemaCode", value: s?.schemaCode ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.schemaName", value: s?.schemaName ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.bin", value: s?.bin ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardNetworkCode", value: s?.cardNetworkCode ?? '' })] }) }));
    const containerStyle = isPageView
        ? { minHeight: 'calc(100vh - 8rem)' }
        : { maxHeight: '82vh', overflowY: 'auto' };
    return (_jsx(PRow, { style: { width: '100%' }, className: "p-mt-2 p-px-2", children: _jsxs(PCol, { col: "12", style: containerStyle, children: [isPageView && (_jsx(BreadCrumb, { home: { icon: 'pi pi-home', command: () => navigate('/') }, model: [
                        { label: translate('form.cms') },
                        { label: translate('form.card'), command: onClose },
                        { label: c?.cardReference || 'Details' },
                    ] })), _jsxs(PCol, { col: "12", className: "p-d-flex p-jc-end p-ai-center p-px-5 w-full", style: { gap: '0.5rem' }, children: [!isHotlisted && c.status !== 'SUSPENDED' && c.status !== 'FROZEN' && (_jsxs(_Fragment, { children: [_jsx(Button, { type: "button", icon: "pi pi-ban", label: translate('entity.action.suspend'), className: "p-button-danger p-button-outlined", disabled: !hasCard || lifecycleBusy, onClick: openSuspendDialog }), _jsx(Button, { type: "button", icon: "pi pi-exclamation-triangle", label: translate('entity.action.hotlistCard'), className: "p-button-danger p-button-outlined", disabled: !hasCard || lifecycleBusy, onClick: openHotlistDialog })] })), !isHotlisted && c.status === 'SUSPENDED' && (_jsx(Button, { type: "button", icon: "pi pi-check-circle", label: translate('entity.action.unsuspend'), className: "p-button-success", disabled: !hasCard || lifecycleBusy, onClick: openUnsuspendDialog }))] }), _jsx(PCol, { col: "12", className: " d-flex justify-content-center align-items-center px-5 mt-4", children: _jsxs(PRow, { children: [_jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardReference", value: c.cardReference ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.maskedPan", value: c.maskedPan ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardHolderName", value: c.cardHolderName ?? '' }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.cardForm", value: c.cardForm }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.status", value: c.status ?? 0 }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.issueDate", value: formatDt(c.issueDate) }), _jsx(OutPutPair, { labelMd: "2", valueMd: "2", label: "form.expiryDate", value: formatDt(c.expiryDate) })] }) }), _jsx(Dialog, { header: translate(suspendDialogMode === 'unsuspend' ? 'entity.action.unsuspend' : 'entity.action.suspend'), visible: suspendDialogVisible, style: { width: 'min(32rem, 95vw)' }, onHide: () => {
                        if (!lifecycleBusy) {
                            setSuspendDialogVisible(false);
                            setSuspendDialogMode('suspend');
                            setSuspendReason('');
                            setSuspendReasonTouched(false);
                        }
                    }, dismissableMask: !lifecycleBusy, closable: !lifecycleBusy, footer: _jsxs(PRow, { className: "p-jc-end", style: { gap: '0.5rem' }, children: [_jsx(Button, { type: "button", label: translate('entity.action.cancel'), className: "p-button-text", disabled: lifecycleBusy, onClick: () => {
                                    setSuspendDialogVisible(false);
                                    setSuspendDialogMode('suspend');
                                    setSuspendReason('');
                                    setSuspendReasonTouched(false);
                                } }), _jsx(Button, { type: "button", label: translate('entity.action.save'), icon: "pi pi-check", loading: lifecycleBusy, disabled: lifecycleBusy || (suspendDialogMode === 'suspend' && !suspendReason.trim()), onClick: confirmLifecycleDialog })] }), children: _jsxs("div", { className: "p-field", children: [_jsx("label", { htmlFor: "card-suspend-reason", children: suspendDialogMode === 'unsuspend'
                                    ? translate('form.unsuspendReason')
                                    : `${translate('form.suspendReason')} *` }), _jsx(InputTextarea, { id: "card-suspend-reason", className: "w-full", rows: 3, value: suspendReason, disabled: lifecycleBusy, invalid: suspendReasonInvalid, maxLength: FIELD_CONSTRAINTS.DESCRIPTION_MAX, onChange: e => setSuspendReason(e.target.value) }), suspendReasonInvalid ? (_jsx("small", { className: "p-error", children: suspendReasonError })) : null] }) }), _jsx(Dialog, { header: translate('entity.action.hotlistCard'), visible: hotlistDialogVisible, style: { width: 'min(32rem, 95vw)' }, onHide: () => {
                        if (!hotlistMutation.isPending) {
                            setHotlistDialogVisible(false);
                            setHotlistReason('');
                            setHotlistReasonTouched(false);
                        }
                    }, dismissableMask: !hotlistMutation.isPending, closable: !hotlistMutation.isPending, footer: _jsxs(PRow, { className: "p-jc-end", style: { gap: '0.5rem' }, children: [_jsx(Button, { type: "button", label: translate('entity.action.cancel'), className: "p-button-text", disabled: hotlistMutation.isPending, onClick: () => {
                                    setHotlistDialogVisible(false);
                                    setHotlistReason('');
                                    setHotlistReasonTouched(false);
                                } }), _jsx(Button, { type: "button", label: translate('entity.action.save'), icon: "pi pi-check", loading: hotlistMutation.isPending, disabled: hotlistMutation.isPending || !hotlistReason.trim(), onClick: confirmHotlistDialog })] }), children: _jsxs("div", { className: "p-field", children: [_jsx("label", { htmlFor: "card-hotlist-reason", children: `${translate('form.hotlistReason')} *` }), _jsx(InputTextarea, { id: "card-hotlist-reason", className: "w-full", rows: 3, value: hotlistReason, disabled: hotlistMutation.isPending, invalid: hotlistReasonInvalid, maxLength: FIELD_CONSTRAINTS.DESCRIPTION_MAX, onChange: e => setHotlistReason(e.target.value) }), hotlistReasonInvalid ? _jsx("small", { className: "p-error", children: hotlistReasonError }) : null] }) }), _jsxs(TabView, { activeIndex: activeIndex, onTabChange: e => setActiveIndex(e.index), children: [_jsxs(TabPanel, { header: translate('general.summary'), children: [summaryFields, _jsxs("div", { className: "p-mt-5", children: [_jsx("h5", { children: translate('form.cardLifecycle') }), loadingLifecycle ? (_jsx(Skeleton, { height: "4rem" })) : lifecycleEvents.length > 0 ? (_jsx("div", { style: { maxWidth: '1200px', margin: '0 auto', padding: '2rem' }, children: _jsx(Timeline, { items: lifecycleEvents, animate: true }) })) : (_jsx("div", { className: "p-text-center p-mt-3", children: translate('card.noLifecycleEvents') }))] })] }), _jsx(TabPanel, { header: translate('form.cardReplacementRequest'), children: hasCard && _jsx(CardReplacementRequestTab, { cardId: c.id }) }), !isHotlisted && (_jsx(TabPanel, { header: translate('form.cardFreezeRequest'), children: hasCard && _jsx(CardFreezeRequest, { cardId: c.id }) })), !isHotlisted && (_jsx(TabPanel, { header: translate('form.cardUnfreezeRequest'), children: hasCard && _jsx(CardUnfreezeRequest, { cardId: c.id }) })), _jsx(TabPanel, { header: translate('form.cardStatement'), children: hasCard && _jsx(CardStatementTab, { cardId: c.id }) })] })] }) }));
};
export default CardDetails;
